import { useState } from "react";
import type { CompareSide, ExpensivenessRating } from "../types";
import { forwardPeg } from "../lib/expensiveness";
import { signedPercent } from "../lib/format";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function multiple(value: number | null | undefined): string {
  return value != null && Number.isFinite(value) && value > 0 ? `${value.toFixed(2)}x` : "—";
}

function Rating({ title, rating }: { title: string; rating?: ExpensivenessRating }) {
  const score = rating?.score;
  const color = score == null ? "text-muted-foreground" : score >= 75
    ? "text-amber-400" : score < 25 ? "text-success" : "text-foreground";
  return <div className="space-y-2 rounded-md border border-border/60 p-3">
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{title}</span>
      <span className={`font-medium ${color}`}>{rating?.label ?? "Awaiting valuation data"}</span>
    </div>
    {score != null && <>
      <div className="flex items-center gap-3">
        <div role="meter" aria-label={title} aria-valuemin={0} aria-valuemax={100} aria-valuenow={score}
          className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-amber-400" style={{ width: `${score}%` }} />
        </div>
        <span className="text-xs tabular-nums">{score}/100</span>
      </div>
      <dl className="space-y-1 text-[11px]">
        {rating?.metrics.map((metric) => <div key={metric.name} className="flex justify-between gap-3"
          title={`Average ${multiple(metric.average)}${metric.start_date ? `; ${metric.start_date} to ${metric.end_date}` : ""}`}>
          <dt className="text-muted-foreground">{metric.name} · {metric.sample_count} {title === "Versus own history" ? "months" : "peers"}</dt>
          <dd className="tabular-nums">Percentile {metric.percentile.toFixed(0)}</dd>
        </div>)}
      </dl>
    </>}
    {title === "Versus own history" && score == null &&
      <p className="text-[11px] text-muted-foreground">{rating?.collected_months ?? 0} prior months collected. Needs 24 months spanning two years for at least two ratios.</p>}
    {title === "Versus peers" && <p className="text-[11px] text-muted-foreground">
      {rating?.group ? `${rating.group} · ${rating.peer_count ?? 0} fresh peers.` : "No comparable category or company industry is available."}
      {score == null && " Needs five other peers for at least two ratios."}
    </p>}
    {!!rating?.symbols?.length && <details className="text-[11px] text-muted-foreground">
      <summary className="cursor-pointer">Peer universe</summary><p>{rating.symbols.join(", ")}</p>
    </details>}
    {title === "Versus own history" && rating?.metrics[0]?.start_date &&
      <p className="text-[11px] text-muted-foreground">Observed {rating.metrics[0].start_date} to {rating.metrics[0].end_date}.</p>}
  </div>;
}

function GrowthScenario({ item }: { item: CompareSide }) {
  const [pe, setPe] = useState(item.forward_price_to_earnings?.toString() ?? "");
  const [growth, setGrowth] = useState(item.earnings_growth_horizon_years === 5
    ? item.long_term_earnings_growth_percent?.toString() ?? "" : "");
  const peg = forwardPeg(Number(pe), Number(growth));
  return <details className="rounded-md border border-border/60 p-3 text-xs">
    <summary className="cursor-pointer font-medium">Explore growth assumptions</summary>
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      <div className="space-y-1">
        <Label htmlFor={`scenario-pe-${item.symbol}`} className="text-xs">Forward P/E estimate</Label>
        <Input id={`scenario-pe-${item.symbol}`} type="number" min="0" step="any"
          value={pe} placeholder="e.g. 25" onChange={(event) => setPe(event.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`scenario-growth-${item.symbol}`} className="text-xs">Annual EPS growth, next 5 years (%)</Label>
        <Input id={`scenario-growth-${item.symbol}`} type="number" step="any" value={growth}
          placeholder="e.g. 15" onChange={(event) => setGrowth(event.target.value)} />
      </div>
    </div>
    <p className="mt-3 font-medium" aria-live="polite">Scenario PEG: {multiple(peg)}</p>
    <p className="mt-1 text-[11px] text-muted-foreground">
      Editable assumptions only; not saved or used in ratings. Forward P/E ÷ annual earnings growth in percent.
      Zero or negative growth cannot produce a meaningful PEG. Do not substitute investment returns for EPS growth.
    </p>
  </details>;
}

function ExpensivenessPanel({ item }: { item: CompareSide }) {
  const data = item.expensiveness;
  const peg = data?.peg;
  const estimate = data?.earnings_estimate;
  return <div className="space-y-3 rounded-md border border-border/60 p-3" data-testid={`expensiveness-${item.symbol}`}>
    <div className="text-sm font-medium">{item.symbol}</div>
    {data && !data.data_fresh && <p className="text-xs text-muted-foreground">Valuation data is missing or older than 90 days; ratings are withheld.</p>}
    <Rating title="Versus own history" rating={data?.historical} />
    <Rating title="Versus peers" rating={data?.peers} />
    <div className="space-y-2 text-xs">
      <div className="flex justify-between gap-3">
        <span className="text-muted-foreground">{peg?.basis === "forward_pe_5y_growth" ? "Forward PEG (5Y earnings growth)" : "PEG (provider reported)"}</span>
        <span className="font-medium tabular-nums">{multiple(peg?.value)}</span>
      </div>
      {peg?.basis === "forward_pe_5y_growth" && <p className="text-[11px] text-muted-foreground">
        {multiple(item.forward_price_to_earnings)} forward P/E ÷ {peg.growth_percent?.toFixed(2)}% expected annual EPS growth over five years.
      </p>}
      {peg?.value != null && peg.basis === "provider_reported" && <p className="text-[11px] text-muted-foreground">Yahoo does not supply the growth horizon or P/E basis for this published PEG. It is not treated as the chart’s forward PEG.</p>}
      {peg?.value == null && <p className="text-[11px] text-muted-foreground">No published PEG or compatible forward P/E and long-term earnings-growth estimate. Use the scenario calculator to explore your own estimates.</p>}
      {peg?.historical_average != null && <p className="text-[11px] text-muted-foreground">
        Historical PEG average {multiple(peg.historical_average)} · {signedPercent(peg.difference_from_average_percent)} relative to that average · {peg.sample_months} observed months.
      </p>}
      {peg?.as_of && <p className="text-[11px] text-muted-foreground">Yahoo Finance · retrieved {peg.as_of.slice(0, 10)}</p>}
    </div>
    {estimate && <div className="rounded-md border border-border/60 p-3 text-xs">
      <div className="font-medium">Next fiscal-year earnings estimates</div>
      <p className="mt-1 text-[11px] text-muted-foreground">Year ending {estimate.end_date} · {estimate.analyst_count == null ? "analyst count unavailable" : `${estimate.analyst_count.toFixed(0)} analysts`}</p>
      <p className="mt-1">EPS consensus {estimate.eps_average?.toFixed(2) ?? "—"}{estimate.currency ? ` ${estimate.currency}` : ""}; range {estimate.eps_low?.toFixed(2) ?? "—"} to {estimate.eps_high?.toFixed(2) ?? "—"}.</p>
      <p className="mt-1 text-[11px] text-muted-foreground">Expected EPS growth {signedPercent(estimate.growth_percent)}. This one-year forecast is not used as five-year growth.</p>
      <p className="mt-1 text-[11px] text-muted-foreground">Yahoo Finance · retrieved {estimate.as_of.slice(0, 10)}</p>
    </div>}
    <GrowthScenario key={`${item.symbol}-${item.forward_price_to_earnings}-${item.long_term_earnings_growth_percent}`} item={item} />
  </div>;
}

export function ExpensivenessComparison({ a, b }: { a: CompareSide; b: CompareSide }) {
  return <Card className="gap-3 py-3" data-testid="expensiveness-comparison">
    <CardHeader className="py-0">
      <CardTitle className="text-base">Expensiveness</CardTitle>
      <CardDescription>How high valuation multiples are versus the same security’s history and comparable peers.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-3 pt-0">
      <div className="grid gap-3 sm:grid-cols-2"><ExpensivenessPanel item={a} /><ExpensivenessPanel item={b} /></div>
      <p className="text-[10px] text-muted-foreground">
        Higher scores mean more expensive, on a 0–100 percentile scale. Equal-weight P/E, P/B, P/S and P/CF ranks;
        at least two ratios are required. Below 25: inexpensive; 25–74: typical; 75–89: expensive; 90–100: very expensive.
        These are comparison bands, not fair-value estimates or investment-quality ratings. Growth, leverage and fund methodology can explain differences.
      </p>
      <p className="text-[10px] text-muted-foreground">
        History uses actual monthly fundamental observations collected by this app, up to ten years, excluding the current month.
        Peer ratings use fresh company-industry or fund-strategy data, not the comparison partner alone.
        EPS forecasts can change; PEG is not a return forecast. The S&P 500’s historical PEG average is not applied to other securities.
      </p>
    </CardContent>
  </Card>;
}
